import { z } from "zod";
import { IsoDateSchema, IsoDateTimeSchema } from "./primitives.js";
/**
 * Progress model (ADR-027 rules 2-3): weigh-ins, 7-day trend, plan
 * consistency, calibration state. Every number here is an OBSERVABLE fact —
 * a count, a percentage, a rolling average — never a prediction or a
 * success/failure verdict (that judgment happens nowhere in this codebase,
 * client or server; see services/planning or infra-cdk shared/progress.ts
 * for the calculator that enforces the sparse-data honesty and calibration
 * gating this schema exposes).
 */
// migration 0023
export const WeightEntrySchema = z.object({
    id: z.string(),
    weightKg: z.number(),
    /** YYYY-MM-DD, one entry per user per calendar date (upsert on conflict). */
    measuredAt: IsoDateSchema,
    createdAt: IsoDateTimeSchema,
});
// POST /v1/progress/weight — logs (or corrects, same date) one weigh-in.
export const ProgressWeightLogRequestSchema = z.object({
    weightKg: z.number().min(20).max(400),
    measuredAt: IsoDateSchema,
});
export const ProgressWeightLogResponseSchema = WeightEntrySchema;
// GET /v1/progress/weight — last 90 days, most recent first.
export const ProgressWeightListResponseSchema = z.object({
    entries: z.array(WeightEntrySchema),
});
/**
 * ADR-027 rule 3 calibration windows:
 *  - calibrating: days 0-7 since the first weigh-in/plan start — no
 *    judgments shown at all, even a raw trend line.
 *  - observing: 8-27 days — trend/consistency ARE shown, but never a
 *    success/failure/plateau verdict.
 *  - reviewable: >=28 days (3-4 consistent weeks) — same honest data, plus
 *    the UI may offer the "review with Amy" CTA.
 */
export const CalibrationStateSchema = z.enum(["calibrating", "observing", "reviewable"]);
// Plan consistency — observable fact only ("you followed X% of your plan"),
// never a predicted outcome. Null when there is no current plan to measure
// against (honest absence, not 0%).
export const ProgressConsistencySchema = z
    .object({
    mealsPlanned: z.number().int().nonnegative(),
    /** outcome in ('had','partial') */
    mealsFollowed: z.number().int().nonnegative(),
    swapsCount: z.number().int().nonnegative(),
    consistencyPercent: z.number().min(0).max(100),
})
    .nullable();
export const ProgressWeightTrendPointSchema = z.object({
    date: IsoDateSchema,
    /** Rolling 7-day average ending on `date`, averaged over however many
     * entries actually exist in that window (min-count guard applies — see
     * the calculator) — gaps are never interpolated. */
    sevenDayAvgKg: z.number(),
    /** How many real entries fed this point's average (>=2 required for the
     * point to appear at all — a single weigh-in is never shown as "the
     * trend"). */
    entryCount: z.number().int().min(2),
});
// Weight trend — null until at least 2 entries exist (ADR-027 rule 2: "never
// a single weigh-in"). trendDirection stays null under the 14-day gate
// (rule 3) even when a series is shown.
export const ProgressWeightTrendSchema = z
    .object({
    series: z.array(ProgressWeightTrendPointSchema),
    totalEntries: z.number().int().nonnegative(),
    /** Null unless >=14 days of data exist AND the series is non-empty. A
     * "flat" band absorbs noise around zero rather than reporting a
     * direction from sub-percent movement. */
    trendDirection: z.enum(["down", "flat", "up"]).nullable(),
})
    .nullable();
// GET /v1/progress/summary
export const ProgressSummaryResponseSchema = z.object({
    calibrationState: CalibrationStateSchema,
    /** Days since the earlier of (first weigh-in, plan start) — null when
     * neither exists yet. */
    daysSinceStart: z.number().int().nonnegative().nullable(),
    consistency: ProgressConsistencySchema,
    weightTrend: ProgressWeightTrendSchema,
});
//# sourceMappingURL=progress.js.map